import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Truck, Instagram, ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "General Enquiry",
  "Cow Milk",
  "Curd",
  "Ghee",
  "Buttermilk",
  "Home Delivery",
  "Farmer Partnership",
  "Retail Partnership",
];

const INSTAGRAM_URL = "https://www.instagram.com/adveshta.cow";
const PHONE_DISPLAY = "+91 81412 61616";
const PHONE_TEL = "+918141261616";
const WHATSAPP_URL = "https://wa.me/918141261616";

const ADDRESSES = [
  {
    id: "gift-city",
    label: "Registered Office",
    name: "Adveshta Projects Private Limited",
    since: "Working since 2022",
    lines: [
      "JM/R-201, Second Floor, Wing-R (Janaadhar Mangla),",
      "Block No-45, Shram Marg, Zone-4 Gift City,",
      "Gandhinagar - 382355, Gujarat",
    ],
  },
  {
    id: "vyara",
    label: "Project Site",
    name: "Adveshta Milk",
    lines: [
      "Khushalpura, Gamit Faliya,",
      "Next to Shiv Temple,",
      "Vyara - 394650, Gujarat",
    ],
  },
];

const inputCls =
  "w-full rounded-xl bg-white border border-sand px-4 py-3.5 text-sm text-coal placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-pine focus:ring-2 focus:ring-pine/15";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "General Enquiry",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const onInterest = (e) =>
      setForm((f) => ({
        ...f,
        interest: INTERESTS.includes(e.detail) ? e.detail : "General Enquiry",
      }));
    window.addEventListener("adveshta:interest", onInterest);
    return () => window.removeEventListener("adveshta:interest", onInterest);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Received — thank you! Our team will reach out to you.", {
        description: `${form.name} · ${form.interest}`,
      });
      setForm({ name: "", phone: "", email: "", interest: "General Enquiry", message: "" });
    } catch {
      toast.error("Could not send right now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const copy = async (addr) => {
    try {
      await navigator.clipboard.writeText([addr.name, ...addr.lines].join(", "));
      setCopied(addr.id);
      toast.success("Address copied to clipboard");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Copy failed — please select the text manually.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-24 lg:py-36">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal>
            <Eyebrow tone="brand">09 — Contact</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-pine">
              Talk to Adveshta.
            </h2>
            <p className="mt-6 text-sage leading-relaxed max-w-lg">
              Enquire about our milk, curd, ghee or buttermilk — or write to us about a farmer or
              retail partnership. This form reaches our team directly, or simply call{" "}
              <a
                data-testid="contact-intro-phone-link"
                href={`tel:${PHONE_TEL}`}
                className="text-pine font-medium underline decoration-gold/60 underline-offset-4 hover:text-royal transition-colors duration-300"
              >
                {PHONE_DISPLAY}
              </a>
              .
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="mt-10 rounded-3xl bg-parchment border border-sand p-7 md:p-9 grid gap-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                    Name *
                  </span>
                  <input
                    data-testid="contact-input-name"
                    className={inputCls}
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                  />
                </label>
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                    Phone *
                  </span>
                  <input
                    data-testid="contact-input-phone"
                    className={inputCls}
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91"
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                    Email
                  </span>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                  />
                </label>
                <label className="grid gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                    Interested In
                  </span>
                  <select
                    data-testid="contact-input-interest"
                    className={inputCls}
                    value={form.interest}
                    onChange={set("interest")}
                  >
                    {INTERESTS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="grid gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                  Message *
                </span>
                <textarea
                  data-testid="contact-input-message"
                  className={`${inputCls} min-h-[120px] resize-y`}
                  required
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what you need — quantity, delivery area, partnership idea…"
                />
              </label>

              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="justify-self-start inline-flex items-center gap-3 rounded-full bg-pine text-cream px-9 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-royal transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:pl-8">
          <div className="grid gap-5 lg:mt-[4.7rem]">
            <Reveal>
              <div
                data-testid="contact-phone-card"
                className="rounded-3xl bg-pine text-cream p-8 md:p-10"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  Call or WhatsApp
                </p>
                <a
                  data-testid="contact-phone-link"
                  href={`tel:${PHONE_TEL}`}
                  className="mt-4 block font-serif text-3xl md:text-4xl tracking-tight hover:text-gold transition-colors duration-300"
                >
                  {PHONE_DISPLAY}
                </a>
                <p className="mt-3 text-sm text-cream/65 leading-relaxed">
                  Orders, home delivery in Vyara, farm-visit confirmations and partnerships.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    data-testid="contact-call-button"
                    href={`tel:${PHONE_TEL}`}
                    className="inline-flex items-center gap-2 rounded-full bg-cream text-pine px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] hover:bg-gold transition-colors duration-300"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call now
                  </a>
                  <a
                    data-testid="contact-whatsapp-button"
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </div>
              </div>
            </Reveal>

            {ADDRESSES.map((addr, i) => (
              <Reveal key={addr.id} delay={0.05 + i * 0.1}>
                <div
                  data-testid={`contact-address-${addr.id}`}
                  className="group relative rounded-3xl border border-sand bg-white p-8 md:p-10 hover:border-gold transition-colors duration-500"
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                      {addr.label}
                    </p>
                    <button
                      data-testid={`contact-copy-${addr.id}`}
                      onClick={() => copy(addr)}
                      className="inline-flex items-center gap-2 rounded-full border border-sand px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage hover:text-pine hover:border-pine transition-colors duration-300"
                    >
                      {copied === addr.id ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" /> Copy
                        </>
                      )}
                    </button>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-pine">{addr.name}</h3>
                  {addr.since && (
                    <p data-testid={`contact-since-${addr.id}`} className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-sage">
                      {addr.since}
                    </p>
                  )}
                  <address className="mt-3 not-italic text-sm leading-relaxed text-sage">
                    {addr.lines.map((l) => (
                      <span key={l} className="block">
                        {l}
                      </span>
                    ))}
                  </address>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.2}>
              <div
                data-testid="contact-delivery-card"
                className="rounded-3xl bg-royal text-cream p-8 md:p-10"
              >
                <div className="flex items-start justify-between gap-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70">
                    Delivery Area
                  </p>
                  <Truck className="h-5 w-5 text-gold shrink-0" />
                </div>
                <p className="mt-4 font-serif text-xl md:text-2xl leading-snug">
                  Fresh home delivery in Vyara city and nearby areas — straight from our farm,
                  every morning.
                </p>
                <p className="mt-4 text-sm text-cream/70 leading-relaxed">
                  Outside Vyara? Find Adveshta products with local retailers and at farmer’s
                  markets, or write to us about a retail partnership.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <a
                data-testid="contact-instagram-card"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between gap-5 rounded-3xl border border-sand bg-white p-7 md:p-8 hover:border-gold transition-colors duration-500"
              >
                <div className="flex items-center gap-5 min-w-0">
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-pine text-cream group-hover:bg-gold group-hover:text-ink transition-colors duration-500">
                    <Instagram className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                      Follow the farm
                    </p>
                    <p className="mt-1.5 font-serif text-xl md:text-2xl text-pine truncate">
                      @adveshta.cow
                    </p>
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-sage shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-pine" />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
